import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, message, Modal } from 'antd';
import { withThemeProvider } from '../utils/withThemeProvider';
import { TEMPLATE_FORM_CONSTANTS } from './constants';
import { StyledModal, AlertBox, AlertTitle, AlertDescription, FormSection, FormLabel, FormTextarea } from './styles';
import FormBasicInfo from './subComponents/FormBasicInfo';
import FormEmailConfig from './subComponents/FormEmailConfig';
import FormAttachments from './subComponents/FormAttachments';
import FormStatus from './subComponents/FormStatus';
import VariablesPanel from './subComponents/VariablesPanel';
import QuickInsertButtons from './subComponents/QuickInsertButtons';
import PrebuiltTemplates from './subComponents/PrebuiltTemplates';
import EditorPanel from './subComponents/EditorPanel';
import PreviewPanel from './subComponents/PreviewPanel';
import {
  insertEmailButton,
  insertEmailDivider,
  insertEmailSpacer,
  insertSocialLinks,
  getPrebuiltTemplate,
  formatHtmlCode,
  insertVariableInEditor,
  resetFormData,
} from './utils/helpers';
import { validateEmailTemplate } from '../utils/EmailTemplateUtils';
import { getDefaultTemplateContent } from '../utils/EmailTemplateUtils';

interface TemplateFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: any;
  onSave: (data: any) => void;
  templateTypes?: Array<{ type: string; label: string }>;
  templateVariables?: (type: string) => Promise<string[]>;
  loading?: boolean;
}

const TemplateFormModal: React.FC<TemplateFormModalProps> = ({
  isOpen,
  onClose,
  template,
  onSave,
  templateTypes = [],
  templateVariables,
  loading = false,
}) => {
  const quillRef = useRef(null);

  const [formData, setFormData] = useState(resetFormData());
  const [variables, setVariables] = useState<string[]>([]);
  const [loadingVariables, setLoadingVariables] = useState(false);
  const [editorMode, setEditorMode] = useState<'visual' | 'code' | 'html'>('visual');
  const [originalHtmlContent, setOriginalHtmlContent] = useState('');

  // Load template data when modal opens
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        type: template.type || '',
        subject: template.subject || '',
        htmlContent: template.htmlContent || '',
        textContent: template.textContent || '',
        status: template.status || TEMPLATE_FORM_CONSTANTS.DEFAULT_VALUES.STATUS,
        isDefault: template.isDefault || false,
        description: template.description || '',
        fromEmail: template.fromEmail || '',
        fromName: template.fromName || '',
        replyTo: template.replyTo || '',
        attachments: template.attachments || [],
      });
      if (template.variables) {
        setVariables(template.variables);
      }
    } else {
      setFormData(resetFormData());
      setVariables([]);
      setEditorMode('visual');
      setOriginalHtmlContent('');
    }
  }, [template, isOpen]);

  // Load variables when template type changes
  useEffect(() => {
    if (formData.type && templateVariables) {
      setLoadingVariables(true);
      templateVariables(formData.type)
        .then(setVariables)
        .catch((error) => {
          console.error('Error loading variables:', error);
          message.error('Failed to load variables');
        })
        .finally(() => setLoadingVariables(false));
    }

    // Auto-populate HTML content when type is selected
    if (formData.type && !formData.htmlContent) {
      const defaultContent = getDefaultTemplateContent(formData.type);
      if (defaultContent) {
        setFormData((prev) => ({
          ...prev,
          htmlContent: defaultContent.htmlContent,
          subject: defaultContent.subject || prev.subject,
          textContent: defaultContent.textContent || prev.textContent,
        }));
      }
    }
  }, [formData.type, formData.htmlContent, templateVariables]);

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleModeSwitch = (newMode: 'visual' | 'code' | 'html') => {
    if (
      editorMode === 'html' &&
      newMode === 'visual' &&
      formData.htmlContent.length > 0
    ) {
      const hasComplexHtml =
        formData.htmlContent.includes('<!DOCTYPE') ||
        formData.htmlContent.includes('table-cell') ||
        formData.htmlContent.includes('style=');

      if (hasComplexHtml) {
        Modal.confirm({
          title: 'Switch to Visual Editor?',
          content:
            'Switching to Visual Editor may remove complex styling and HTML structure. Your original HTML will be preserved in Code/HTML modes. Continue?',
          onOk() {
            setOriginalHtmlContent(formData.htmlContent);
            setEditorMode(newMode);
          },
        });
        return;
      }
    }

    if (
      newMode === 'html' &&
      originalHtmlContent &&
      originalHtmlContent !== formData.htmlContent
    ) {
      Modal.confirm({
        title: 'Restore Original HTML?',
        content:
          'Would you like to restore your original HTML template? This will replace any changes made in the Visual Editor.',
        onOk() {
          handleFormChange('htmlContent', originalHtmlContent);
          setEditorMode(newMode);
        },
        onCancel() {
          setEditorMode(newMode);
        },
      });
      return;
    }

    setEditorMode(newMode);
  };

  const handleSave = () => {
    const validation = validateEmailTemplate(formData);

    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join('; ');
      message.error(`Validation Error: ${errorMessages}`);
      return;
    }

    const payload = {
      ...formData,
      variables,
    };

    onSave(payload);
  };

  const handleClose = () => {
    setFormData(resetFormData());
    setVariables([]);
    setEditorMode('visual');
    setOriginalHtmlContent('');
    onClose();
  };

  const handleLoadTemplate = (type: string) => {
    const template = getPrebuiltTemplate(type);
    handleFormChange('htmlContent', template);
  };

  const handleInsertVariable = (variable: string) => {
    insertVariableInEditor(editorMode, variable, quillRef, formData.htmlContent, handleFormChange);
  };

  return (
    <StyledModal
      title={template ? TEMPLATE_FORM_CONSTANTS.TITLES.EDIT : TEMPLATE_FORM_CONSTANTS.TITLES.CREATE}
      open={isOpen}
      onCancel={handleClose}
      width="90%"
      style={{ maxWidth: '1400px' }}
      footer={null}
    >
      <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', paddingRight: '12px' }}>
        <AlertBox type="warning" style={{ marginBottom: '16px' }}>
          <AlertTitle>⚠️ {TEMPLATE_FORM_CONSTANTS.MESSAGES.REQUIRED_FIELDS}</AlertTitle>
          <AlertDescription>{TEMPLATE_FORM_CONSTANTS.MESSAGES.REQUIRED_FIELDS_DESC}</AlertDescription>
        </AlertBox>

        <FormBasicInfo
          formData={formData}
          templateTypes={templateTypes}
          onFormChange={handleFormChange}
        />

        <FormEmailConfig formData={formData} onFormChange={handleFormChange} />

        <FormAttachments
          attachments={formData.attachments}
          onAttachmentsChange={(attachments) => handleFormChange('attachments', attachments)}
        />

        <FormStatus formData={formData} onFormChange={handleFormChange} />

        {formData.type && (
          <VariablesPanel
            variables={variables}
            loading={loadingVariables}
            onInsertVariable={handleInsertVariable}
          />
        )}

        <FormSection style={{ marginTop: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 250px', gap: '24px' }}>
            <EditorPanel
              editorMode={editorMode}
              onModeChange={handleModeSwitch}
              formData={formData}
              onFormChange={handleFormChange}
              quillRef={quillRef}
              originalHtmlContent={originalHtmlContent}
            />
            <div>
              {editorMode === 'visual' && (
                <QuickInsertButtons
                  onInsertButton={() => insertEmailButton(quillRef, formData.htmlContent)}
                  onInsertDivider={() => insertEmailDivider(quillRef, formData.htmlContent)}
                  onInsertSpacer={() => insertEmailSpacer(quillRef, formData.htmlContent)}
                  onInsertSocialLinks={() => insertSocialLinks(quillRef, formData.htmlContent)}
                />
              )}

              {editorMode === 'html' && (
                <PrebuiltTemplates
                  onTemplateSelect={handleLoadTemplate}
                  onClearAll={() => handleFormChange('htmlContent', '')}
                  onFormatCode={() => handleFormChange('htmlContent', formatHtmlCode(formData.htmlContent))}
                  onRestoreOriginal={() => handleFormChange('htmlContent', originalHtmlContent)}
                  canRestore={originalHtmlContent && originalHtmlContent !== formData.htmlContent}
                />
              )}
            </div>
          </div>
        </FormSection>

        <PreviewPanel formData={formData} variables={variables} />

        <FormSection style={{ marginTop: '24px' }}>
          <FormLabel>{TEMPLATE_FORM_CONSTANTS.LABELS.PLAIN_TEXT_CONTENT}</FormLabel>
          <FormTextarea
            value={formData.textContent}
            onChange={(e) => handleFormChange('textContent', e.target.value)}
            placeholder={TEMPLATE_FORM_CONSTANTS.PLACEHOLDERS.PLAIN_TEXT}
            rows={4}
            style={{ minHeight: '100px' }}
          />
        </FormSection>

        <FormSection style={{ marginTop: '24px', borderTop: '1px solid #d9d9d9', paddingTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <Button onClick={handleClose}>
              {TEMPLATE_FORM_CONSTANTS.BUTTONS.CANCEL}
            </Button>
            <Space>
              {!template && (
                <Button onClick={() => setFormData(resetFormData())}>
                  {TEMPLATE_FORM_CONSTANTS.BUTTONS.CLEAR_FORM}
                </Button>
              )}
              <Button
                type="primary"
                onClick={handleSave}
                loading={loading}
              >
                {template ? TEMPLATE_FORM_CONSTANTS.BUTTONS.UPDATE : TEMPLATE_FORM_CONSTANTS.BUTTONS.CREATE}
                {' '}{TEMPLATE_FORM_CONSTANTS.BUTTONS.TEMPLATE}
              </Button>
            </Space>
          </div>
        </FormSection>
      </div>
    </StyledModal>
  );
};

export const TemplateFormModalWithTheme = withThemeProvider(TemplateFormModal);

export default TemplateFormModal;
